import { useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { fetchCallRecordsForAICoworker } from 'flow/actions/ai-coworker/call-records-actions';
import SquareButtonsContainer from 'flow/components/contact-card/SquareButtonContainer';
import ProfileItem from 'flow/components/headers/ProfileItem';
import Page from 'flow/components/Setting/Page';
import UserAvatar from 'flow/components/ui-components/AiUserAvatar';
import { extractContactInitials } from 'flow/components/ui-components/ExtensionContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtensionActiveProfile,
    selectExtensionStatus,
} from 'flow/selectors/ExtensionSelectors';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import {
    getCountryCode,
    getFixedNumberFromContact,
    getPhoneNumberFromContact,
} from 'flow/utils/extension-utils';
import { getContactBackgroundColor } from 'flow/utils/extension-utils-TW';
import AiCoworkerCallHistory from 'flow/pages/ai-coworker/AiCoworkerCallHistory';
import AICoworkerAgentStatisticsPreview from 'flow/components/ai-coworker/AICoworkerAgentStatisticsPreview';

const AiCoworker = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const extension: TypedMap<Extension> = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey]),
    );
    const isAdmin = useAppSelector(selectIsAdmin);

    const dispatch = useAppDispatch();
    const initials = extractContactInitials(extension.get('contact'));

    const currentProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extension.get('key')),
    );

    const userCountryCode = getCountryCode(extension.get('contact'), null);
    const fixedNumber = getFixedNumberFromContact(extension.get('contact'));
    const phoneNumber = getPhoneNumberFromContact(extension.get('contact'));
    const mainNumber = fixedNumber ?? phoneNumber;
    const prettyNumber = prettyFormatE164Number(mainNumber, userCountryCode);
    const status = useAppSelector((state) =>
        selectExtensionStatus(state, extensionKey),
    );
    useEffect(() => {
        void dispatch(fetchCallRecordsForAICoworker(extensionKey));
    }, [dispatch, extensionKey]);

    return (
        <Page
            header={extension.getIn(['contact', 'firstName'])}
            className="no-main-margin bg-gray-150 dark:bg-white"
            mainId="main-scrollable-div"
            action={
                isAdmin ? (
                    <IconButton
                        tooltipLabel={i18next.t('settings')}
                        onClick={() =>
                            history.push(
                                generatePath(
                                    routes[
                                        'pbx.aicoworker.extensionKey.settings'
                                    ],
                                    {
                                        extensionKey,
                                    },
                                ),
                            )
                        }
                    >
                        <Icon iconKey="setting_menu" />
                    </IconButton>
                ) : null
            }
        >
            <div className="flex flex-col gap-3">
                <div className="shadow-base xs:flex-nowrap flex grow flex-wrap items-center gap-6 rounded-xl bg-white p-6 dark:bg-gray-100">
                    <div className="flex self-start">
                        <UserAvatar
                            className={getContactBackgroundColor(
                                extension.get('contact'),
                            )}
                            status={status}
                            size="extraLarge"
                        >
                            {initials}
                        </UserAvatar>
                    </div>
                    <div className="@container relative z-10 flex w-full flex-wrap items-center gap-3 lg:justify-end">
                        <ProfileItem
                            extensionKey={extension.get('key')}
                            contact={extension.get('contact')}
                            activeProfile={currentProfile}
                            title={prettyNumber}
                            skipAdminCheck={true}
                        />

                        <SquareButtonsContainer
                            className="items-end self-end"
                            contact={extension.get('contact')}
                            extensionKey={extension.get('key')}
                            shouldNotifyPresence={false}
                            allowPresenceNotification={false}
                            disableVideoCall={true}
                            disableMessaging={true}
                        />
                    </div>
                </div>
                <AICoworkerAgentStatisticsPreview extensionKey={extensionKey} />
                <div className="history shadow-base rounded-xl bg-white p-6 dark:bg-gray-100">
                    <h3 className="pt-2 pb-6 text-xl font-bold">
                        {i18next.t('call.history')}
                    </h3>
                    <AiCoworkerCallHistory extensionKey={extensionKey} />
                </div>
            </div>
        </Page>
    );
};

export default AiCoworker;
